import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FormatDateRange,
  FormatTimeRanges,
  formatDateRange,
} from "../../../../MultiCardModal/components/DateRangeSearch";
import TimeRangePicker from "../../../../MultiCardModal/components/TimeRangePicker";
import ScalingDateRangePicker from "../../../../ScalingDateRangePicker";
import { TimeRange } from "../../../SearchProvider";
import SearchCard, { OpenDialogProps } from "./SearchCard";

const showTimeRangeInLabelMaxWidth = 170 as const;

export type DateRange = {
  from: Date;
  until: Date;
};

type DateRangeSearchProps = OpenDialogProps & {
  from: Date | null;
  setFrom: Dispatch<SetStateAction<Date | null>>;
  until: Date | null;
  setUntil: Dispatch<SetStateAction<Date | null>>;
  timeRanges: TimeRange[];
  setTimeRanges: Dispatch<SetStateAction<TimeRange[]>>;
};

export default function DateRangeSearch({
  from,
  until,
  setFrom,
  setUntil,
  timeRanges,
  setTimeRanges,
  ...props
}: DateRangeSearchProps) {
  const searchCardRef = useRef<HTMLDivElement>(null);
  const [searchCardWith, setSearchCardWith] = useState(0);
  useEffect(() => {
    if (!searchCardRef.current) return;

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentRect) setSearchCardWith(entry.contentRect.width);
      });
    });

    observer.observe(searchCardRef.current);

    setSearchCardWith(searchCardRef.current.offsetWidth);

    return () => observer.disconnect();
  }, []);

  const showTimeRangeInLabel = useMemo(
    () => searchCardWith < showTimeRangeInLabelMaxWidth,
    [searchCardWith]
  );

  const formattedDateRange = useMemo(
    () =>
      showTimeRangeInLabel
        ? formatDateRange(from, until)
        : FormatDateRange({
            from,
            until,
            timeRanges,
          }),
    [from, until, timeRanges, showTimeRangeInLabel]
  );

  const label = useMemo(
    () => (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        When {showTimeRangeInLabel && FormatTimeRanges({ timeRanges })}
      </div>
    ),
    [timeRanges, showTimeRangeInLabel]
  );

  return (
    <SearchCard
      label={label}
      value={formattedDateRange}
      ref={searchCardRef}
      {...props}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
          padding: "32px",
        }}
      >
        <TimeRangePicker
          timeRanges={timeRanges}
          setTimeRanges={setTimeRanges}
        />
        <div>
          <ScalingDateRangePicker
            from={from}
            until={until}
            setFrom={setFrom}
            setUntil={setUntil}
            numCalenders={2}
          />
        </div>
      </div>
    </SearchCard>
  );
}
